import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useRef, useState } from 'react';
import { Alert, Button, Form, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { toast } from 'react-toastify';
import EventModalMediaContent from '../../../../../components/EventModalMediaContent';
import { useOrderContext } from '../../../../../providers/OrderProvider';
import { useOrderDetailViewOnly } from './OrderDetailViewOnlyContext';
import { useOrderPerformanceFeatures } from '../../../../../providers/OrderPerformanceFeaturesProvider';
import { useAdjustFinalCostMutation, useConfirmFinalCostMutation } from '../../../../../slices/ordersApiSlice';
import { markLocalOrderMutation } from '../../../../../utils/order/orderLocalMutationGuard';
import formatCurrency from '../../../../../utils/formatCurrency';
import { getOrderPerformanceBasisView } from '../../../../../utils/order/orderPerformanceBasisView';
import { useOrderCorrectionDraft } from './OrderCorrectionDraftContext';

const formatDateTime = (value) => {
	if (!value) return '-';
	const d = new Date(value);
	if (Number.isNaN(d.getTime())) return '-';
	return d.toLocaleString('zh-CN', { hour12: false });
};

const OrderFinalCostSection = ({ forceVisible = false }) => {
	const { orderDetailUi } = useOrderPerformanceFeatures();
	const { order, isOrderReady } = useOrderContext();
	const viewOnly = useOrderDetailViewOnly();
	const { hasPending: hasPendingCorrections, isSaving: isSavingCorrections, savePending } = useOrderCorrectionDraft();
	const [confirmFinalCost, { isLoading: isConfirming }] = useConfirmFinalCostMutation();
	// 连点防护：isConfirming 依赖重渲染才生效，重组件树下快速连点会漏；ref 同步置位堵住间隙
	const confirmInFlightRef = useRef(false);
	// 确认成功后 isFinalCostConfirmed 要等订单刷新才为 true，这段空窗用 state 把按钮置灰，避免只被 ref 静默拦住
	const [isConfirmedPendingRefresh, setIsConfirmedPendingRefresh] = useState(false);
	const [adjustFinalCost, { isLoading: isAdjusting }] = useAdjustFinalCostMutation();
	const [showAdjustModal, setShowAdjustModal] = useState(false);
	const [showConfirmFinalCostModal, setShowConfirmFinalCostModal] = useState(false);
	const [adjustedCost, setAdjustedCost] = useState('');
	const [adjustReason, setAdjustReason] = useState('');

	const orderOId = order?._id;
	const isFinalCostConfirmed = Boolean(order?.isFinalCostConfirmed);
	const isCompleted = Boolean(order?.isCompleted);
	const uiHints = order?.uiHints;
	const viewerCapabilities = order?.viewerCapabilities ?? {};
	const hintsReady = Boolean(uiHints);

	const canViewSettlement = viewerCapabilities.canViewPerformanceSettlement;
	const canConfirmRole = viewerCapabilities.canConfirmFinalCost;
	const canAdjust = Boolean(viewerCapabilities.canAdjustFinalCost) && isCompleted && isFinalCostConfirmed;
	const confirmFinalCostBlockReason = uiHints?.confirmFinalCostBlockReason ?? null;
	const { hasBasis, hasUnmatched, unmatchedIssues: unmatchedBasisIssues } = getOrderPerformanceBasisView(order);
	const pendingFinalCostAdjustment = order?.pendingFinalCostAdjustment;

	const purchasingCostTotal = uiHints?.purchasingCostTotal ?? 0;
	const confirmModalBrandEntries = uiHints?.brandCorrectionEntriesForDisplay ?? [];
	const brandCorrectionTotal = uiHints?.brandCorrectionTotal ?? 0;
	const suggestedFinalCost = uiHints?.suggestedFinalCost ?? 0;

	if (!canViewSettlement || (!orderDetailUi && !forceVisible) || !orderOId || uiHints?.isLegacyCompletedBeforeFinalCostCutoff) return null;

	const openConfirmFinalCostModal = async () => {
		if (!canConfirmRole || isFinalCostConfirmed || confirmInFlightRef.current) return;
		if (!hintsReady) {
			toast.warn('订单展示信息加载中，请稍候');
			return;
		}
		if (confirmFinalCostBlockReason) {
			toast.warn(confirmFinalCostBlockReason);
			return;
		}
		if (!hasBasis) {
			toast.error('请先在「绩效规则」中配置或重匹配绩效规则');
			return;
		}
		if (hasUnmatched) {
			const roleHint = unmatchedBasisIssues.map((issue) => issue.roleLabel).join('、');
			toast.error(`请先在「绩效规则」中处理未匹配项：${roleHint}`);
			return;
		}
		if (isSavingCorrections) {
			toast.warn('校正额保存中，请稍候');
			return;
		}
		let nextSuggestedFinalCost = suggestedFinalCost;
		if (hasPendingCorrections) {
			try {
				const saved = await savePending();
				const savedSuggested = Number(saved?.correctionView?.suggestedFinalCost);
				if (!Number.isNaN(savedSuggested)) nextSuggestedFinalCost = savedSuggested;
			} catch {
				return;
			}
		}
		if (nextSuggestedFinalCost < 0) {
			toast.error('最终成本不能为负，请检查品牌校正额');
			return;
		}
		setShowConfirmFinalCostModal(true);
	};

	const handleConfirmFinalCost = async () => {
		if (confirmInFlightRef.current || isConfirming || isFinalCostConfirmed) return;
		confirmInFlightRef.current = true;
		try {
			markLocalOrderMutation(orderOId);
			const result = await confirmFinalCost({ orderId: orderOId }).unwrap();
			setShowConfirmFinalCostModal(false);
			setIsConfirmedPendingRefresh(true);
			const confirmedCost = Number(result?.finalCost);
			toast.success(`已确认最终成本 ${formatCurrency(Number.isNaN(confirmedCost) ? suggestedFinalCost : confirmedCost)}`);
		} catch (error) {
			const apiMessage = error?.data?.message || error?.error || error?.message;
			// 连点/超时：服务端可能已确认成功，提示刷新核对，避免只看到空的「失败」
			if (typeof apiMessage === 'string' && apiMessage.includes('最终成本已确认')) {
				setShowConfirmFinalCostModal(false);
				setIsConfirmedPendingRefresh(true);
				toast.success(apiMessage);
				return;
			}
			// 只有真失败才放开在途标记：确认成功后 isFinalCostConfirmed 要等订单刷新才为 true，期间按钮仍可见，必须继续拦住
			confirmInFlightRef.current = false;
			toast.error(apiMessage || '确认最终成本失败，请刷新订单后核对是否已生效');
		}
	};

	const handleSubmitAdjustment = async () => {
		const parsedAdjustedCost = Number(adjustedCost);
		if (Number.isNaN(parsedAdjustedCost) || parsedAdjustedCost < 0) {
			toast.error('修正后的成本必须大于等于 0');
			return;
		}
		if (!adjustReason.trim()) {
			toast.error('修正原因不能为空');
			return;
		}

		try {
			markLocalOrderMutation(orderOId);
			await adjustFinalCost({
				orderId: orderOId,
				adjustedCost: parsedAdjustedCost,
				reason: adjustReason.trim(),
			}).unwrap();
			toast.success('最终成本修正已提交');
			setShowAdjustModal(false);
			setAdjustedCost('');
			setAdjustReason('');
		} catch (error) {
			toast.error(error?.data?.message || error?.message || '提交最终成本修正失败');
		}
	};

	const confirmedFinalCost = Number(order?.finalCost) || 0;
	const canShowConfirmAction = !isFinalCostConfirmed && canConfirmRole;
	const suggestedFinalCostHint =
		suggestedFinalCost >= 0
			? `系统将按进价合计与品牌校正合计确认最终成本（推算 ${formatCurrency(suggestedFinalCost)}，仅供参考）`
			: '系统将按进价合计与品牌校正合计确认最终成本';
	const canShowFinalCostAction = !viewOnly && (canShowConfirmAction || canAdjust);
	const confirmButtonDisabled =
		!isOrderReady || isConfirming || isConfirmedPendingRefresh || isSavingCorrections || !hintsReady || Boolean(confirmFinalCostBlockReason) || !hasBasis || hasUnmatched;

	const finalCostContent = (
		<ul className='fs-10 text-600 mb-0 list-unstyled'>
			<li>
				<span className='me-3'>
					<span className='d-inline-block rounded-circle bg-300 me-2' style={{ width: 6, height: 6, verticalAlign: 'middle' }}></span>
					{isFinalCostConfirmed ? `已确认: ${formatCurrency(confirmedFinalCost)}` : '待确认最终成本'}
				</span>
			</li>
			<li>
				<span className='me-3'>
					<span className='d-inline-block rounded-circle bg-300 me-2' style={{ width: 6, height: 6, verticalAlign: 'middle' }}></span>
					总价: {formatCurrency(order?.totalPrice)}
				</span>
			</li>
			<li>
				<span className='me-3'>
					<span className='d-inline-block rounded-circle bg-300 me-2' style={{ width: 6, height: 6, verticalAlign: 'middle' }}></span>
					进价: {hintsReady ? formatCurrency(purchasingCostTotal) : '—'}
				</span>
			</li>
			<li>
				<span className='me-3'>
					<span className='d-inline-block rounded-circle bg-300 me-2' style={{ width: 6, height: 6, verticalAlign: 'middle' }}></span>
					品牌校正: {hintsReady ? formatCurrency(brandCorrectionTotal) : '—'}
				</span>
			</li>
			{isFinalCostConfirmed ? (
				<li>
					<span className='me-3'>
						<span className='d-inline-block rounded-circle bg-300 me-2' style={{ width: 6, height: 6, verticalAlign: 'middle' }}></span>
						{formatDateTime(order?.finalCostConfirmedAt)} · {order?.finalCostConfirmedResult?.operator || '-'}
					</span>
				</li>
			) : null}
		</ul>
	);

	const finalCostAction = canShowFinalCostAction ? (
		<div className='d-flex flex-column gap-2 align-items-end'>
			{canShowConfirmAction ? (
				<OverlayTrigger placement='top' overlay={<Tooltip id='confirm-final-cost-hint'>{suggestedFinalCostHint}</Tooltip>}>
					<span className='d-inline-block'>
						<Button size='sm' className='fs-11' variant='outline-warning' disabled={confirmButtonDisabled} onClick={openConfirmFinalCostModal}>
							{isConfirming ? '确认中' : isConfirmedPendingRefresh ? '已确认' : '确认'}
						</Button>
					</span>
				</OverlayTrigger>
			) : null}
			{canAdjust ? (
				<Button
					size='sm'
					className='fs-11'
					variant='outline-warning'
					onClick={() => setShowAdjustModal(true)}
					disabled={!isOrderReady || isAdjusting || Boolean(pendingFinalCostAdjustment)}
				>
					修正
				</Button>
			) : null}
		</div>
	) : null;

	return (
		<>
			<EventModalMediaContent icon='sack-dollar' heading='最终成本' headingAction={finalCostAction}>
				<div className='d-flex flex-column gap-2'>
					{finalCostContent}
					{pendingFinalCostAdjustment ? (
						<Alert variant='info' className='py-2 px-3 mb-0 fs-10'>
							<FontAwesomeIcon icon='hourglass-half' className='me-1' />
							已有待审批修正
						</Alert>
					) : null}
				</div>
			</EventModalMediaContent>

			{/* 确认中锁死所有关闭途径：请求已到服务端无法中止，关掉弹窗会让人误以为取消成功 */}
			<Modal
				show={showConfirmFinalCostModal}
				onHide={() => {
					if (isConfirming) return;
					setShowConfirmFinalCostModal(false);
				}}
				backdrop={isConfirming ? 'static' : true}
				keyboard={!isConfirming}
				centered
				className='modal-nested-stack'
				backdropClassName='modal-backdrop-nested'
			>
				<Modal.Header closeButton={!isConfirming}>
					<Modal.Title className='fs-6'>确认最终成本{order?.orderId ? ` · ${order.orderId}` : ''}</Modal.Title>
				</Modal.Header>
				<Modal.Body className='fs-10'>
					<div className='bg-100 rounded p-3'>
						<div className='text-600'>将确认的最终成本</div>
						<div className='fs-5 fw-bold text-900 my-1'>{formatCurrency(suggestedFinalCost)}</div>
						<div className='text-600'>
							进价合计 {formatCurrency(purchasingCostTotal)} ＋ 校正合计 {formatCurrency(brandCorrectionTotal)}
						</div>
						<div className='d-flex justify-content-between mt-2 pt-2 border-top'>
							<span className='text-600'>订单总价</span>
							<span className='fw-semibold'>{formatCurrency(order?.totalPrice)}</span>
						</div>
					</div>
					<div className='mt-3 mb-2'>
						<div className='fw-semibold mb-1'>品牌校正额（请核对是否有漏填）</div>
						{confirmModalBrandEntries.length > 0 ? (
							<ul className='list-unstyled mb-0'>
								{confirmModalBrandEntries.map((row) => (
									<li key={row.brand} className='d-flex justify-content-between py-1 border-bottom'>
										<span>{row.brand}</span>
										<span className={Number(row.correctionAmount) === 0 ? 'text-muted' : ''}>{formatCurrency(row.correctionAmount)}</span>
									</li>
								))}
							</ul>
						) : (
							<p className='text-muted mb-0'>暂无品牌明细</p>
						)}
					</div>
					<Alert variant='warning' className='py-2 px-3 mb-0 mt-3 fs-10'>
						确认后最终成本不可直接修改，并作为绩效结算依据；如需调整须提交「修正」审批。上方金额为按当前数据推算，实际写入以确认时服务端重算为准。
					</Alert>
					{isConfirming ? (
						<Alert variant='info' className='py-2 px-3 mb-0 mt-3 fs-10'>
							<FontAwesomeIcon icon='hourglass-half' className='me-1' />
							确认已提交，无法中断；如需调整请在确认完成后使用「修正」
						</Alert>
					) : null}
				</Modal.Body>
				<Modal.Footer>
					<Button variant='outline-secondary' size='sm' disabled={isConfirming} onClick={() => setShowConfirmFinalCostModal(false)}>
						取消
					</Button>
					<Button variant='primary' size='sm' onClick={handleConfirmFinalCost} disabled={isConfirming || isConfirmedPendingRefresh || isSavingCorrections} title={suggestedFinalCostHint}>
						{isConfirming ? '确认中...' : '确认最终成本'}
					</Button>
				</Modal.Footer>
			</Modal>

			<Modal show={showAdjustModal} onHide={() => setShowAdjustModal(false)} centered className='modal-nested-stack' backdropClassName='modal-backdrop-nested'>
				<Modal.Header closeButton>
					<Modal.Title className='fs-6'>申请修正最终成本</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<Form.Group className='mb-3' controlId='orderAdjustedCost'>
						<Form.Label className='small fw-semibold'>修正后成本</Form.Label>
						<Form.Control
							name='orderAdjustedCost'
							type='number'
							min='0'
							step='0.01'
							value={adjustedCost}
							onChange={(e) => setAdjustedCost(e.target.value)}
							placeholder='请输入修正后成本'
						/>
					</Form.Group>
					<Form.Group controlId='orderAdjustReason'>
						<Form.Label className='small fw-semibold'>修正原因</Form.Label>
						<Form.Control
							name='orderAdjustReason'
							as='textarea'
							rows={3}
							value={adjustReason}
							onChange={(e) => setAdjustReason(e.target.value)}
							placeholder='请输入修正原因'
						/>
					</Form.Group>
				</Modal.Body>
				<Modal.Footer>
					<Button variant='outline-secondary' size='sm' onClick={() => setShowAdjustModal(false)}>
						取消
					</Button>
					<Button variant='primary' size='sm' onClick={handleSubmitAdjustment} disabled={isAdjusting}>
						{isAdjusting ? '提交中...' : '提交申请'}
					</Button>
				</Modal.Footer>
			</Modal>
		</>
	);
};

OrderFinalCostSection.propTypes = {
	forceVisible: PropTypes.bool,
};

export default OrderFinalCostSection;
